import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Icon,
  Text,
} from '@chakra-ui/react';
import {
  ConfidenceLevel,
  MarketDataPercentages,
  MarketDataSourceType,
  UserObject,
} from '@client/graphql/__generated__/types';
import { CircleAlertIcon } from 'lucide-react';
import { ToggleButtons } from '~/common/components/ToggleButtons/ToggleButtons';
import { SignalStrength } from '~/common/icons/SignalIcon';
import { NotesPopover } from '../NotesPopover';
import { SourceTags } from '../SourceTags';
import { getMarketDataDisplay } from '../common.utils';

interface ContingenciesMobileCardProps {
  label: string;
  source?: MarketDataSourceType;
  confidence?: ConfidenceLevel;
  marketData?: MarketDataPercentages;
  distanceMiles?: number;
  timePeriodMonths?: number;
  errorMessage?: string;
  value?: boolean;
  onUpdateValue?: (value: boolean) => void;
  notes?: string | null;
  onUpdateNotes?: (notes: string | null) => void;
  buyersAgentUser?: Partial<Pick<UserObject, 'fullName' | 'profilePicture'>>;
  isViewOnly?: boolean;
}

export const ContingenciesMobileCard = ({
  label,
  marketData,
  source,
  confidence,
  distanceMiles,
  timePeriodMonths,
  errorMessage,
  value = false,
  onUpdateValue,
  notes,
  onUpdateNotes,
  buyersAgentUser,
  isViewOnly = false,
}: ContingenciesMobileCardProps) => {
  const marketDataDisplay = marketData
    ? getMarketDataDisplay(marketData, 'yes')
    : undefined;

  const sourceLabel = source?.toLowerCase()?.split('_')?.join(' ');

  return (
    <Card pt={3}>
      <CardHeader px={3} py={0}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontWeight="medium">{label}</Text>
          <Flex minWidth={6}>
            <NotesPopover
              isViewOnly={isViewOnly}
              label={label}
              noteUser={buyersAgentUser}
              notes={notes ?? ''}
              onChange={(notes) => onUpdateNotes?.(notes)}
            />
          </Flex>
        </Flex>
      </CardHeader>
      <CardBody p={1} py={0}>
        <Flex direction="column" gap={2} justifyContent="center" px={2}>
          <Flex alignItems="center" gap={2}>
            <Flex
              direction="column"
              height="100%"
              justifyContent="center"
              py={isViewOnly ? 0 : 4}
              width="100%"
            >
              {!isViewOnly ? (
                <Flex gap={4} height="100%">
                  <Flex flexBasis={0} flexGrow={1} height="100%">
                    <Flex direction="column" gap={1}>
                      <Text
                        color="whiteAlpha.600"
                        fontSize="sm"
                        fontWeight="medium"
                        textTransform="uppercase"
                      >
                        Market Data
                      </Text>
                      {!!marketData && !errorMessage && (
                        <Flex direction="column" gap={1}>
                          {marketDataDisplay}
                          <SourceTags
                            confidence={confidence as unknown as SignalStrength}
                            distanceMiles={distanceMiles}
                            isExpanded={false}
                            sourceLabel={sourceLabel}
                            timePeriodMonths={timePeriodMonths}
                          />
                        </Flex>
                      )}
                      {errorMessage && (
                        <Flex alignItems="center" gap={1}>
                          <Icon as={CircleAlertIcon} color="red.500" />
                          <Text>{errorMessage}</Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                  <Flex direction="column" flexBasis={0} flexGrow={1} gap={2}>
                    <Text
                      color="whiteAlpha.600"
                      fontSize="sm"
                      fontWeight="medium"
                      textTransform="uppercase"
                    >
                      Your Recommendation
                    </Text>
                    <Flex gap={2} justifyContent="center">
                      {isViewOnly ? (
                        <Text
                          fontSize={{ base: '20px', md: '24px' }}
                          fontWeight="medium"
                        >
                          {value ? 'Yes' : 'No'}
                        </Text>
                      ) : (
                        <ToggleButtons
                          activeButton={value}
                          activeButtonColor="purpleGradient"
                          activeButtonTextColor="white"
                          buttons={[
                            {
                              label: 'Yes',
                              value: true,
                            },
                            {
                              label: 'No',
                              value: false,
                            },
                          ]}
                          setActiveButton={(value) => onUpdateValue?.(value)}
                          size="md"
                        />
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              ) : (
                <Flex
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                >
                  <Text fontSize="24px" fontWeight="medium">
                    {value ? 'Yes' : 'No'}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </CardBody>
      {isViewOnly && !errorMessage && (
        <CardFooter>
          <Flex justifyContent="space-between" width="100%">
            <Flex direction="column" gap={1}>
              <Text
                color="whiteAlpha.600"
                fontSize="sm"
                fontWeight="medium"
                textTransform="uppercase"
              >
                Market Data
              </Text>
              {marketDataDisplay}
            </Flex>
            <SourceTags
              confidence={confidence as unknown as SignalStrength}
              distanceMiles={distanceMiles}
              isExpanded={false}
              sourceLabel={sourceLabel}
              timePeriodMonths={timePeriodMonths}
            />
          </Flex>
        </CardFooter>
      )}
    </Card>
  );
};
